import React, { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Globe } from "lucide-react";
import { useI18n, SUPPORTED } from "../i18n/I18nContext";

const SECTIONS = [
  { key: "overview", to: "#overview" },
  { key: "sectors", to: "#sectors" },
  { key: "process", to: "#process" },
  { key: "insights", to: "#insights" },
  { key: "newsletter", to: "#newsletter" }
];

export default function Header() {
  const { t, lang, switchLang, localePath } = useI18n();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);

  const isHome = location.pathname === localePath("") || location.pathname === localePath("/");

  const anchorHref = (hash) => (isHome ? hash : localePath("") + hash);

  const closeAll = () => {
    setOpen(false);
    setLangOpen(false);
  };

  return (
    <header
      className="sticky top-0 z-40 w-full border-b border-[#1a2f4b] bg-[#0B2447] text-white"
      data-testid="site-header"
    >
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <Link
          to={localePath("")}
          onClick={closeAll}
          data-testid="header-logo-link"
          className="flex items-center gap-2 font-serif text-xl tracking-tight"
        >
          <span
            aria-hidden
            className="inline-block h-6 w-6 border-2 border-[#C56E4E]"
            style={{ transform: "rotate(45deg)" }}
          />
          <span className="uppercase tracking-[0.18em]">Xorvyniq</span>
        </Link>

        <nav
          className="hidden items-center gap-7 lg:flex"
          data-testid="header-desktop-nav"
        >
          {SECTIONS.map((s) => (
            <a
              key={s.key}
              href={anchorHref(s.to)}
              data-testid={`nav-desktop-${s.key}`}
              className="text-sm uppercase tracking-[0.14em] text-slate-200 hover:text-[#C56E4E]"
            >
              {t(`nav.${s.key}`)}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <div className="relative">
            <button
              type="button"
              onClick={() => setLangOpen((v) => !v)}
              aria-haspopup="listbox"
              aria-expanded={langOpen}
              data-testid="lang-switcher-btn"
              className="flex items-center gap-2 border border-[#2a4a75] px-3 py-2 text-xs uppercase tracking-[0.14em] text-slate-100 hover:border-[#C56E4E] hover:text-[#C56E4E]"
            >
              <Globe className="h-4 w-4" aria-hidden />
              <span>{lang.toUpperCase()}</span>
            </button>
            {langOpen && (
              <ul
                role="listbox"
                data-testid="lang-switcher-list"
                className="absolute right-0 mt-1 w-28 border border-[#2a4a75] bg-[#0B2447] text-white shadow-xl"
              >
                {SUPPORTED.map((code) => (
                  <li key={code}>
                    <button
                      type="button"
                      data-testid={`lang-option-${code}`}
                      onClick={() => {
                        switchLang(code);
                        setLangOpen(false);
                      }}
                      className={`block w-full px-3 py-2 text-left text-xs uppercase tracking-[0.14em] hover:bg-[#12325a] ${
                        code === lang ? "text-[#C56E4E]" : "text-slate-100"
                      }`}
                    >
                      {code === "ar" ? "ES · AR" : "EN"}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <button
            type="button"
            className="lg:hidden inline-flex h-10 w-10 items-center justify-center border border-[#2a4a75] text-white hover:border-[#C56E4E] hover:text-[#C56E4E]"
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? t("nav.menu_close") : t("nav.menu_open")}
            data-testid="mobile-menu-toggle"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {open && (
        <nav
          data-testid="mobile-nav"
          className="lg:hidden border-t border-[#1a2f4b] bg-[#0B2447]"
        >
          <ul className="mx-auto max-w-7xl px-4 py-4 sm:px-6">
            {SECTIONS.map((s) => (
              <li key={s.key}>
                <a
                  href={anchorHref(s.to)}
                  data-testid={`nav-mobile-${s.key}`}
                  className="block py-3 text-sm uppercase tracking-[0.14em] text-slate-100 hover:text-[#C56E4E]"
                  onClick={closeAll}
                >
                  {t(`nav.${s.key}`)}
                </a>
              </li>
            ))}
          </ul>
        </nav>
      )}
    </header>
  );
}
